import { Resources } from 'constants/resources';

import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useDelete, useNotify, useRecordContext } from 'react-admin';
import { Button, CancelButton } from 'shared/mui/Buttons';
import { Card, CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog, DialogProps } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Typography, TypographyWithBold } from 'shared/mui/Typography';
import { useSyncNestedDialogState } from 'shared/widgets/CommissionsTabs/context/CommissionsNestedDialogsContext';

type Props = {
  onClose: VoidFunction;
} & DialogProps;

export const CommissionsTabsTaskStatusDeleteDialog: React.FC<Props> = ({ open, onClose }) => {
  const translate = useTypedTranslate();
  const record = useRecordContext();
  const notify = useNotify();

  useSyncNestedDialogState(open);

  const [deleteOne, { isLoading }] = useDelete();

  const handleDelete = () => {
    if (!record?.id) {
      return;
    }

    deleteOne(
      Resources.CommissionRecalculationTasks,
      { id: record.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [Resources.CommissionRecalculationTasks] });
          queryClient.invalidateQueries({ queryKey: ['commission_recalculation_statuses'] });

          notify(
            <TypographyWithBold
              text={translate('app.commissionsRecalculation.deleteDialog.success', {
                name: record?.commission?.name,
              })}
            />,
            { type: 'success' },
          );
          onClose();
        },
        onError: (error: any) => {
          notify(`Ошибка при удалении: ${error.message}`, { type: 'error' });
        },
      },
    );
  };

  return (
    <Dialog onClose={onClose} open={open} width="640px">
      <Card>
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title={translate('app.commissionsRecalculation.deleteDialog.title')}
          titleSize="smallBold"
        />
        <CardContent>
          <Typography>{translate('app.commissionsRecalculation.deleteDialog.message')}</Typography>
        </CardContent>
        <CardActionsButtons
          rightActionsSlot={[
            <CancelButton key="cancel-button" onClick={onClose} variant="outlined" />,
            <Button
              color="error"
              isLoading={isLoading}
              key="save-button"
              label={translate('app.buttons.delete')}
              onClick={handleDelete}
              variant="contained"
            />,
          ]}
        />
      </Card>
    </Dialog>
  );
};
